import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MUITypography from "../../Components/MUITypography";
import { VM_Colors } from "../../Styles/VMStyles";

export const DEMO_VIDEO = {
  id: "xfRigAHqQdU",
  title: "Demo Video",
};

export const HOW_TO_PLAYLIST_ID = "PLHUJrnp2hv5g";

export const HOW_TO_VIDEOS = [
  { id: "a9tuRFkCP5s", title: "How to Register" },
  { id: "EwRHEP4pj8s", title: "How to Add a New Enquiry" },
  { id: "T-MEGGm7-8M", title: "How to Schedule Appointments & Follow-ups" },
  { id: "sNSELB6epdI", title: "How to Add Event Photos" },
  { id: "oqbHUynuPXY", title: "How to Add Event Vendors" },
  { id: "JsWxK8Ba0xQ", title: "How to Generate & Update Invoice" },
  { id: "5BXwmTGWpvo", title: "How to Allocate Rooms" },
  { id: "o6cl4OUndTg", title: "How to Create Roles" },
  { id: "sLXhR_Ncluc", title: "How to Add Team Members" },
  { id: "U3Rx-wpj3K8", title: "Visitor Management" },
  { id: "AQLakgohbFY", title: "How to Create and Assign Tasks" },
  { id: "s6kGjA56HnQ", title: "How to Collect Customer Feedback" },
];

const TAB_DEMO = "demo";
const TAB_HOW_TO = "howto";
const ACTIVE_TEAL = VM_Colors.turquoise;
const PLAYLIST_CARD_WIDTH = 148;
const PLAYLIST_GAP = 12;
const PLAYLIST_SCROLL_BY = (PLAYLIST_CARD_WIDTH + PLAYLIST_GAP) * 2;
const YOUTUBE_WATCH = (id) => `https://www.youtube.com/watch?v=${id}`;
const YOUTUBE_PLAYLIST = `https://www.youtube.com/playlist?list=${HOW_TO_PLAYLIST_ID}`;

const YouTubeEmbed = ({ videoId, title }) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      aspectRatio: "16 / 9",
      borderRadius: 2.5,
      overflow: "hidden",
      backgroundColor: "#000",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
    }}
  >
    <Box
      key={videoId}
      component="iframe"
      src={`https://www.youtube.com/embed/${videoId}?rel=0`}
      title={title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
      }}
    />
  </Box>
);

const TabButton = ({ active, icon, label, onClick }) => (
  <Button
    fullWidth
    onClick={onClick}
    startIcon={icon}
    sx={{
      height: 48,
      borderRadius: 2,
      textTransform: "none",
      fontWeight: 700,
      fontSize: { xs: 14, sm: 16 },
      color: active ? "#fff" : VM_Colors.primary,
      backgroundColor: active ? ACTIVE_TEAL : "#fff",
      border: active ? "none" : `1.5px solid ${VM_Colors.primary}`,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: active ? ACTIVE_TEAL : "#f8fafc",
        border: active ? "none" : `1.5px solid ${VM_Colors.primary}`,
      },
    }}
  >
    {label}
  </Button>
);

const playlistArrowSx = (side) => ({
  position: "absolute",
  top: 42,
  [side]: { xs: 0, sm: -8 },
  zIndex: 2,
  width: 36,
  height: 36,
  backgroundColor: "#fff",
  boxShadow: "0 2px 10px rgba(15, 23, 42, 0.18)",
  color: VM_Colors.primary,
  "&:hover": {
    backgroundColor: "#fff",
  },
  "&.Mui-disabled": {
    backgroundColor: "#fff",
    color: "#9ca3af",
    opacity: 0.45,
  },
});

function TutorialsContent() {
  const [tab, setTab] = useState(TAB_DEMO);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const playlistRef = useRef(null);

  const isHowTo = tab === TAB_HOW_TO;
  const selectedVideo = HOW_TO_VIDEOS[selectedIndex];
  const activeVideo = isHowTo ? selectedVideo : DEMO_VIDEO;
  const lessonNumber = selectedIndex + 1;

  const updatePlaylistScroll = useCallback(() => {
    const el = playlistRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    if (!isHowTo) return undefined;
    const el = playlistRef.current;
    if (!el) return undefined;

    const frame = requestAnimationFrame(updatePlaylistScroll);
    el.addEventListener("scroll", updatePlaylistScroll, { passive: true });
    window.addEventListener("resize", updatePlaylistScroll);

    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("scroll", updatePlaylistScroll);
      window.removeEventListener("resize", updatePlaylistScroll);
    };
  }, [isHowTo, updatePlaylistScroll]);

  const scrollPlaylist = (direction) => {
    const el = playlistRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * PLAYLIST_SCROLL_BY, behavior: "smooth" });
  };

  const handleSelectVideo = (index) => {
    setSelectedIndex(index);
    const el = playlistRef.current;
    const card = el?.children?.[index];
    if (!el || !card) return;
    const offset = card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, offset), behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 3, md: 5 } }}>
      <MUITypography
        align="center"
        sx={{
          fontWeight: 800,
          fontSize: { xs: 28, md: 36 },
          color: "#111827",
          mb: 1.5,
        }}
      >
        Tutorials
      </MUITypography>
      <MUITypography
        align="center"
        sx={{
          color: "#6b7280",
          fontSize: { xs: 14, md: 16 },
          lineHeight: 1.6,
          maxWidth: 520,
          mx: "auto",
          mb: 3,
        }}
      >
        Watch the product demo, then follow the step-by-step guides to use every
        feature in Venue Manager CRM.
      </MUITypography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
        <TabButton
          active={tab === TAB_DEMO}
          icon={<PlayArrowIcon />}
          label="Demo Video"
          onClick={() => setTab(TAB_DEMO)}
        />
        <TabButton
          active={isHowTo}
          icon={<MenuBookOutlinedIcon />}
          label="How To Use"
          onClick={() => setTab(TAB_HOW_TO)}
        />
      </Stack>

      <YouTubeEmbed videoId={activeVideo.id} title={activeVideo.title} />

      <Box sx={{ textAlign: "center", mt: 2.5 }}>
        {isHowTo && (
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.4,
              mb: 1.25,
              borderRadius: 999,
              backgroundColor: "#d7eef8",
              color: VM_Colors.primary,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {`Lesson ${lessonNumber} of ${HOW_TO_VIDEOS.length}`}
          </Box>
        )}

        <MUITypography
          sx={{
            fontWeight: 800,
            fontSize: { xs: 18, md: 22 },
            color: "#111827",
            mt: isHowTo ? 0 : 0.5,
          }}
        >
          {isHowTo ? `${lessonNumber}. ${selectedVideo.title}` : DEMO_VIDEO.title}
        </MUITypography>

        <Stack
          direction="row"
          spacing={3}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 1.25, flexWrap: "wrap" }}
        >
          <Link
            href={YOUTUBE_WATCH(activeVideo.id)}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              color: "#374151",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <YouTubeIcon sx={{ color: "#FF0000", fontSize: 22 }} />
            Watch on YouTube
          </Link>
          {isHowTo && (
            <Link
              href={YOUTUBE_PLAYLIST}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                color: "#374151",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <FormatListBulletedIcon sx={{ color: ACTIVE_TEAL, fontSize: 20 }} />
              Full playlist
            </Link>
          )}
        </Stack>
      </Box>

      {isHowTo && (
        <Box sx={{ mt: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <MUITypography sx={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>
              Tutorial playlist
            </MUITypography>
            <MUITypography sx={{ color: "#9ca3af", fontSize: 14, fontWeight: 600 }}>
              {`${HOW_TO_VIDEOS.length} videos`}
            </MUITypography>
          </Stack>

          <Box sx={{ position: "relative" }}>
            <IconButton
              aria-label="Previous videos"
              onClick={() => scrollPlaylist(-1)}
              disabled={!canScrollLeft}
              sx={playlistArrowSx("left")}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              aria-label="Next videos"
              onClick={() => scrollPlaylist(1)}
              disabled={!canScrollRight}
              sx={playlistArrowSx("right")}
            >
              <ChevronRightIcon />
            </IconButton>

            <Box
              ref={playlistRef}
              sx={{
                display: "flex",
                gap: `${PLAYLIST_GAP}px`,
                overflowX: "auto",
                pb: 1,
                px: { xs: 4.5, sm: 3 },
                scrollBehavior: "smooth",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
            {HOW_TO_VIDEOS.map((video, index) => {
              const selected = index === selectedIndex;

              return (
                <Box
                  key={video.id}
                  onClick={() => handleSelectVideo(index)}
                  sx={{
                    width: PLAYLIST_CARD_WIDTH,
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: selected
                        ? `2px solid ${ACTIVE_TEAL}`
                        : "2px solid transparent",
                    }}
                  >
                    <Box
                      component="img"
                      src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                      alt={video.title}
                      sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.95)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 18, color: "#111" }} />
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        right: 6,
                        bottom: 6,
                        px: 0.75,
                        py: 0.15,
                        borderRadius: 0.75,
                        backgroundColor: "rgba(0,0,0,0.78)",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {`${index + 1}/${HOW_TO_VIDEOS.length}`}
                    </Box>
                  </Box>
                  <MUITypography
                    sx={{
                      mt: 0.75,
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.35,
                      color: selected ? ACTIVE_TEAL : "#111827",
                    }}
                  >
                    {`${index + 1}. ${video.title}`}
                  </MUITypography>
                </Box>
              );
            })}
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default TutorialsContent;
